import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { usePageTitle } from '@/components/layout/page-title';
import { PageHeader } from '@/components/layout/PageHeader';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { RefreshButton } from '@/features/invoices/RefreshButton';
import { useScrollToTop } from '@/features/invoices/useRefreshList';
import { useOutboundInvoices, useRefreshOutbound, type OutboundInvoice } from './api';
import { OutboundCardList, OutboundTable } from './OutboundTable';

/**
 * The "Outgoing" register (`/invoices/sent`): every invoice we have issued (or are issuing) to KSeF, with
 * its live pipeline status, newest first. Selecting a row opens its status/timeline page. This is the
 * write-side counterpart to the read-side "Issued" list (which shows the settled KSeF-synced record);
 * here the source of truth is our own outbound state machine.
 */
export function OutboundListPage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const isMobile = useIsMobile();
  usePageTitle(t('invoices.heading.sent'));

  const navigate = useNavigate();
  const query = useOutboundInvoices();
  const refresh = useRefreshOutbound();
  // Refresh collapses the list back to page 1, so send the scroll position back up to match.
  const { rootRef, scrollToTop } = useScrollToTop();

  const items = useMemo(() => {
    const seen = new Set<string>();
    const flat: OutboundInvoice[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const invoice of page.items) {
        if (!seen.has(invoice.id)) {
          seen.add(invoice.id);
          flat.push(invoice);
        }
      }
    }
    return flat;
  }, [query.data]);

  // Stable identity so the memoized rows don't all re-render when a page is appended.
  const onOpen = useCallback((id: string) => navigate(`/invoices/sent/${id}`), [navigate]);

  const loadMore = {
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    onLoadMore: query.fetchNextPage,
  };

  return (
    <div ref={rootRef} className="flex h-full min-h-0 flex-col gap-6">
      <PageHeader
        title={t('invoices.outbound.title')}
        description={t('invoices.outbound.description')}
        actions={<RefreshButton {...refresh} onClick={scrollToTop} />}
      />

      {query.isPending ? (
        <Skeleton className={isMobile ? 'h-64 w-full' : 'min-h-0 w-full flex-1'} />
      ) : query.isError ? (
        <MessageCard isMobile={isMobile} tone="destructive">
          {t('invoices.outbound.load_error')}
        </MessageCard>
      ) : items.length === 0 ? (
        <MessageCard isMobile={isMobile}>
          <p className="text-sm">{t('invoices.outbound.empty')}</p>
          <p className="max-w-sm text-xs">{t('invoices.outbound.empty_hint')}</p>
        </MessageCard>
      ) : isMobile ? (
        // The card list fills the remaining height and scrolls inside its own region, so the page
        // never grows a native scrollbar — consistent with the desktop table and the other lists.
        <Card className="min-h-0 flex-1 py-0">
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            <ScrollArea className="cn-vertical-scroll min-h-0 flex-1">
              <OutboundCardList items={items} lng={lng} {...loadMore} />
            </ScrollArea>
          </CardContent>
        </Card>
      ) : (
        // Desktop: the table fills the remaining viewport height and scrolls inside its own ScrollArea,
        // so the page never grows a native scrollbar.
        <Card className="min-h-0 flex-1">
          <CardContent className="flex min-h-0 flex-1 flex-col">
            <OutboundTable items={items} lng={lng} onOpen={onOpen} {...loadMore} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * A status message (empty / error) that occupies the same height as the populated list on desktop, so
 * the container size never changes with content. Stays natural-flow on mobile, where the page scrolls.
 */
function MessageCard({
  isMobile,
  tone = 'muted',
  children,
}: {
  isMobile: boolean;
  tone?: 'muted' | 'destructive';
  children: ReactNode;
}) {
  return (
    <Card className={isMobile ? undefined : 'min-h-0 flex-1'}>
      <CardContent
        className={cn(
          'flex flex-col items-center gap-2 py-6 text-center text-sm',
          tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground',
          isMobile ? undefined : 'min-h-0 flex-1 justify-center',
        )}
      >
        {children}
      </CardContent>
    </Card>
  );
}
